import { get } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdAdd,
  MdArrowBackIos,
  MdArrowForwardIos,
  MdCheckCircle,
  MdRemove,
} from "react-icons/md";
import Button from "shared/components/Button";
import Card from "shared/components/Card";
import Header from "shared/components/Header";
import { COLORS } from "shared/style/colors";
import { NAME_SPACES } from "shared/locales/constants";

const TELECONSULTATION_PLANS = [
  { id: "solo", name: "Téléconsultation Solo", price: 9.9, description: "Accès individuel au service de téléconsultation", features: ["Consultations illimitées", "Accès 7j/7 24h/24", "Ordonnances en ligne", "Médecins généralistes"] },
  { id: "duo", name: "Téléconsultation Duo", price: 14.9, description: "Accès pour 2 personnes du même foyer", features: ["2 bénéficiaires", "Consultations illimitées", "Accès 7j/7 24h/24", "Ordonnances en ligne", "Médecins généralistes"] },
  { id: "famille", name: "Téléconsultation Famille", price: 19.9, description: "Accès illimité pour tous les membres du foyer", features: ["Bénéficiaires illimités", "Consultations illimitées", "Accès 7j/7 24h/24", "Ordonnances en ligne", "Médecins généralistes", "Pédiatres disponibles"] },
];

const CASHBACK_PLANS = [
  { id: "sans", name: "Sans", price: 0, description: "Pas d'accès au programme cashback" },
  { id: "cigale", name: "Cigale", price: 0, priceLabel: "Gratuite", description: "Accès gratuit aux offres partenaires", features: ["Accès aux offres", "1 deal par mois", "Cashback jusqu'à 5%"] },
  { id: "fourmi", name: "Fourmi", price: 3.95, description: "Accès élargi aux offres premium", features: ["Accès élargi", "3 deals par mois", "Cashback jusqu'à 10%"] },
  { id: "fourgale", name: "Fourgale", price: 5.95, description: "Accès premium complet illimité", features: ["Accès premium", "Deals illimités", "Cashback jusqu'à 15%"] },
];

const PlanCard = ({ plan, selected, onSelect }) => (
  <div
    className={`card-product--container ${selected ? "added" : "default"}`}
    onClick={() => onSelect(plan.id)}
    style={{ cursor: "pointer" }}
  >
    <div className="card-product--wrapper">
      <div className="product-info">
        <div className="description--wrapper">
          <div className="text--wrapper">
            <h3 className="text-md">{plan.name}</h3>
            <span className="text-sm">{plan.description}</span>
          </div>
          <div className="price--wrapper">
            <span className="text-sm">
              <span className="cost-per-month">
                {plan.priceLabel || `${plan.price.toFixed(2)} €`}
              </span>
              {plan.price > 0 ? " /mois" : ""}
            </span>
          </div>
        </div>
      </div>
      <div className="action--wrapper">
        <button className="action">
          {selected ? (
            <MdCheckCircle size={24} color={COLORS.C_WHITE} />
          ) : (
            <MdAdd size={24} color={COLORS.C_WHITE} />
          )}
        </button>
      </div>
    </div>
    {plan.features && (
      <div style={{ padding: "0 16px 16px" }}>
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          {plan.features.map((f) => (
            <li key={f} className="text-sm">
              {f}
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

const SelectProducts = ({ project, onNext, onBack }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const DETAIL = t("DETAIL", { returnObjects: true });
  const [nextLoading, setNextLoading] = useState(false);
  const recommendedFormula = get(project, "fields.recommendedFormula", "Solo");
  const defaultTelecons = recommendedFormula === "Famille" ? "famille" : recommendedFormula === "Duo" ? "duo" : "solo";

  const [selectedTeleconsultation, setSelectedTeleconsultation] = useState(get(project, "fields.selectedTeleconsultation", defaultTelecons));
  const [selectedCashback, setSelectedCashback] = useState(get(project, "fields.selectedCashback", "sans"));

  const teleconsPrice = TELECONSULTATION_PLANS.find((p) => p.id === selectedTeleconsultation)?.price || 0;
  const cashbackPrice = CASHBACK_PLANS.find((p) => p.id === selectedCashback)?.price || 0;
  const totalPrice = teleconsPrice + cashbackPrice;

  const handleSubmit = () => {
    setNextLoading(true);
    onNext({
      payload: {
        fields: {
          ...project.fields,
          selectedTeleconsultation,
          selectedCashback,
          teleconsPrice,
          cashbackPrice,
          totalPrice,
          selectedToCompare: [],
        },
      },
      onCompleted: () => setNextLoading(false),
      onError: () => setNextLoading(false),
    });
  };

  return (
    <>
      <Header key="header" title="Sélection des Produits" actions={[]} />
      <div className="template-product-list--container">
        <div className="template-product-list--wrapper">
          <Card title="Téléconsultation Médicale" subtitle="Formule adaptée à la composition du foyer">
            <div className="grid--1">
              {TELECONSULTATION_PLANS.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  selected={selectedTeleconsultation === plan.id}
                  onSelect={setSelectedTeleconsultation}
                />
              ))}
            </div>
          </Card>

          <Card title="Programme Cashback La Fourgale" subtitle="Option facultative">
            <div className="grid--1">
              {CASHBACK_PLANS.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  selected={selectedCashback === plan.id}
                  onSelect={setSelectedCashback}
                />
              ))}
            </div>
          </Card>

          <Card type="simple">
            <div className="d-flex justify--between align--center">
              <div>
                <h4>Montant total mensuel</h4>
                <span>Calcul en temps réel</span>
              </div>
              <h2 className="text--primary">{totalPrice.toFixed(2)} €<small>/mois</small></h2>
            </div>
          </Card>

          <div className="d-flex justify--between m-top-20">
            <Button onClick={onBack} type="secondary--link">
              <MdArrowBackIos size={16} />
              {DETAIL.TEMPLATES.FILTERS.BUTTON.TEXT.PREVIOUS}
            </Button>
            <Button type="primary" onClick={handleSubmit} loading={nextLoading}>
              {DETAIL.TEMPLATES.FILTERS.BUTTON.TEXT.NEXT}
              <MdArrowForwardIos size={16} />
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SelectProducts;
